import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Loader2, Mail, Plus, Trash2, UserRound } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const empty = { name: "", email: "", phone: "", license_number: "",
  business_name: "", tax_id: "", address: "", city: "", state: "", zip: "" };

const Field = ({ label, testId, ...rest }) => (
  <div>
    <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">{label}</label>
    <Input data-testid={testId} className="h-11 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" {...rest} />
  </div>
);

export default function Chauffeurs() {
  const { company } = useAuth();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);

  const load = () => api.get("/chauffeurs").then(({ data }) => setItems(data)).catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/chauffeurs", form);
      setForm(empty);
      load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="chauffeurs-page" className="space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-gold">Team</p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-50">Chauffeurs</h1>
        <p className="mt-2 text-sm text-slate-400">
          Manifests are emailed to the chauffeur's registered address so they can accept or decline.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <form onSubmit={submit} className="space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6 lg:col-span-2">
          <h2 className="text-lg font-medium text-slate-100">Add chauffeur</h2>
          <Field label="Full name" testId="chauffeur-name" value={form.name} onChange={set("name")} required placeholder="Michael Reyes" />
          <Field label="Email" testId="chauffeur-email" type="email" value={form.email} onChange={set("email")} required placeholder="michael@company.com" />
          <Field label="Phone" testId="chauffeur-phone" value={form.phone} onChange={set("phone")} placeholder="713-555-9090" />
          <Field label="License number" testId="chauffeur-license" value={form.license_number} onChange={set("license_number")} placeholder="TX-4409821" />

          <div className="rounded-lg border border-ink-600 bg-ink-900/50 p-4">
            <button type="button" data-testid="chauffeur-tax-toggle" onClick={() => setTaxOpen(!taxOpen)}
              className="flex w-full items-center justify-between font-mono text-[11px] uppercase tracking-widest text-slate-400 hover:text-gold">
              1099 tax details · optional
              <ChevronDown className={`h-4 w-4 transition-transform ${taxOpen ? "rotate-180" : ""}`} />
            </button>
            {taxOpen && (
              <div data-testid="chauffeur-tax-fields" className="mt-4 space-y-4">
                <p className="text-xs text-slate-500">
                  Only needed at tax time — fill these in whenever you're ready and the year-end
                  1099-NEC worksheets fill themselves out.
                </p>
                <Field label="Business name" testId="chauffeur-business-name" value={form.business_name} onChange={set("business_name")} placeholder="Reyes Livery LLC" />
                <Field label="TIN / SSN" testId="chauffeur-tax-id" value={form.tax_id} onChange={set("tax_id")} placeholder="12-3456789" />
                <Field label="Mailing address" testId="chauffeur-address" value={form.address} onChange={set("address")} placeholder="1204 Westheimer Rd" />
                <div className="grid grid-cols-3 gap-3">
                  <Field label="City" testId="chauffeur-city" value={form.city} onChange={set("city")} placeholder="Houston" />
                  <Field label="State" testId="chauffeur-state" value={form.state} onChange={set("state")} placeholder="TX" />
                  <Field label="ZIP" testId="chauffeur-zip" value={form.zip} onChange={set("zip")} placeholder="77006" />
                </div>
              </div>
            )}
          </div>
          {msg && <p data-testid="chauffeur-msg" className="text-sm text-emerald-400">{msg}</p>}
          {error && (
            <p data-testid="chauffeur-error" className="text-sm text-red-400">
              {error}{" "}
              {error.includes("plan allows") && <Link to="/app/plans" className="underline">Upgrade</Link>}
            </p>
          )}
          <Button data-testid="chauffeur-save-btn" type="submit" disabled={busy} className="h-11 w-full bg-gold text-ink-900 hover:bg-gold-light">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Plus className="mr-2 h-4 w-4" /> Add chauffeur</>)}
          </Button>
          <p data-testid="chauffeur-limit-note" className="font-mono text-[11px] text-slate-500">
            {company?.plan_name} plan · {items.length} of{" "}
            {company?.chauffeur_limit ? company.chauffeur_limit : "unlimited"} chauffeurs
          </p>
        </form>

        <div className="space-y-3 lg:col-span-3">
          {items.length === 0 && (
            <p data-testid="chauffeurs-empty" className="rounded-xl border border-ink-600 bg-ink-800 p-8 text-center text-sm text-slate-500">
              No chauffeurs yet.
            </p>
          )}
          {items.map((c) => (
            <div key={c.id} data-testid={`chauffeur-card-${c.id}`} className="card-hover flex items-start justify-between gap-4 rounded-xl border border-ink-600 bg-ink-800 p-5">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <UserRound className="h-4 w-4 text-gold" />
                  <p className="truncate text-base font-medium text-slate-100">{c.name}</p>
                </div>
                <p className="mt-1.5 flex items-center gap-1.5 truncate text-xs text-slate-400">
                  <Mail className="h-3 w-3" /> {c.email}
                </p>
                <p className="mt-1 text-xs text-slate-500">{[c.phone, c.license_number].filter(Boolean).join(" · ")}</p>
                <p data-testid={`chauffeur-tax-state-${c.id}`} className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-600">
                  {c.tax_id ? "1099 details on file" : "1099 details missing"}
                </p>
                <p data-testid={`chauffeur-access-${c.id}`} className="mt-1.5 font-mono text-[10px] uppercase tracking-widest">
                  {c.activated ? (
                    <span className="text-emerald-400">Can sign in</span>
                  ) : c.invite_email_status === "failed" ? (
                    <span className="text-red-400">Invite email failed — check the address</span>
                  ) : c.invited ? (
                    <span className="text-amber-300">Invited — waiting for them to set a password</span>
                  ) : (
                    <span className="text-slate-500">No login yet</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {!c.activated && (
                  <button
                    data-testid={`chauffeur-resend-${c.id}`}
                    onClick={async () => {
                      setError(""); setMsg("");
                      try {
                        await api.post(`/chauffeurs/${c.id}/resend-invite`);
                        setMsg(`Invitation sent to ${c.email}.`);
                        load();
                      } catch (e) { setError(errMsg(e)); }
                    }}
                    className="font-mono text-[10px] uppercase tracking-widest text-gold hover:underline"
                  >
                    Send invite
                  </button>
                )}
                <button
                  data-testid={`chauffeur-delete-${c.id}`}
                  onClick={async () => { await api.delete(`/chauffeurs/${c.id}`); load(); }}
                  className="text-slate-500 transition-colors hover:text-red-400"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
